import React, { useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

import { useGame, useDictionary } from 'common/state';
import { getAttemptsData } from 'common/logic';
import End from 'components/End';
import MedDagen from 'components/MedDagen';

function Board() {
  const [gameState, gameActions] = useGame();
  const [dictionary] = useDictionary();
  const endOfBoardRef = useRef(null);

  const { answer } = gameState.spec;
  const { status, typed, cursorIndex, attempts } = gameState.data;

  const attemptsData = getAttemptsData(answer, attempts);

  const activeClass = (i) =>
    cursorIndex === i && status === 'selecting' ? 'active' : '';

  const handleKeyDown = useCallback(
    ({ key }) => {
      gameActions.input(key, dictionary);
    },
    [dictionary, gameActions],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    endOfBoardRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [attempts, status]);

  const letterAnimation = (index) => ({
    initial: { rotateY: 90 },
    animate: { rotateY: 0 },
    transition: { duration: 0.5, delay: index * 0.1 },
  });

  return (
    <div className="board-wrapper">
      <div className="board">
        {attemptsData.map((attemptData, i) => (
          <div className="guess" key={i}>
            {attemptData.map(({ letter, classes }, j) => (
              <motion.div
                className={classes.join(' ')}
                key={j}
                initial={letterAnimation(j).initial}
                animate={letterAnimation(j).animate}
                transition={letterAnimation(j).transition}
              >
                {letter}
              </motion.div>
            ))}
          </div>
        ))}
        {gameState.data.status === 'selecting' && (
          <div className="typing">
            <div className="letters">
              {typed.split('').map((letter, i) => (
                <div className="letter" key={i}>
                  {letter}
                </div>
              ))}
            </div>
            <div className="cursors">
              <div
                className={`cursor ${activeClass(0)}`}
                onClick={() => gameActions.positionCursor(0)}
              ></div>
              {typed.split('').map((_, i) => (
                <div
                  className={`cursor ${activeClass(i + 1)}`}
                  key={i}
                  onClick={() => gameActions.positionCursor(i + 1)}
                ></div>
              ))}
            </div>
          </div>
        )}
        {gameState.data.status === 'won' && (
          <>
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              {gameState.id === '199' && <MedDagen />}
              <End />
            </motion.div>
          </>
        )}
        <div ref={endOfBoardRef} />
      </div>
    </div>
  );
}

export default Board;
